import React from "react";
import { Divider } from "@mui/material";

import { useAppSelector, selectNoti, updateNoti, updateNumberNoti, resetNoti, selectAuth, updateNotificationStatus } from "@redux";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { StyledNotificationComponent, NotificationItem, NextButton, StyledToggleButtonShort } from ".";
import { CircularLoader, DocumentBorder } from "..";
import { appRouteEnums, ApproveTypeEnums, FilterNotificationEnums, NotificationLinkEnums, NotificationTypeEnums, FilterNotificationTypeEnums, FilterNotificationTypeNameEnums, FilterNotificationStatusEnums } from "@enums";
import { notiApi } from "@api";
import { MESSAGE_DATA_NOT_FOUND } from "@configs";
import { debugLog, measureElement } from "@utils";

/*
 * 各通知レコードのクリック処理のリファクタリングについて
 * 
 * 各通知レコードのクリック処理は、dataカラムのtypeキーの値によって分岐する。
 * また、クリックしたレコードのstatusが0（未読）か1（既読）かによっても処理が分岐していた。
 * 
 * このため、各typeごとにstatusが0（未読）と1（既読）の場合の処理を持たせられる型(NotificationHandler)を用意した。
 * 
 * statusが0（未読）の場合は、typeに関係なく共通してAPIを叩いてDBのstatusを1（既読）に
 * 変更する処理を行っていたため、この部分は共通化している。
 * 
 * 将来的に新しいtypeが追加された場合、そのtypeに対応するハンドラーを追加するだけで対応できる設計になっている。
 */
// ハンドラー型定義（未読・既読の場合の処理）
type NotificationHandler = (values: any) => void;

// 通知タイプごとのハンドラーマップ型定義
interface NotificationHandlers {
  [key: string | number]: {              // 注：keyは動的プロパティ名
    read: NotificationHandler;           // 既読の場合の処理
    unread: NotificationHandler;         // 未読の場合の処理
  };
}

/**
 * 通知処理を行うカスタムフック
 * 
 * このフックは通知のクリックイベントを処理し、通知の種類（type）と状態（status）に応じて処理を実行する。
 * 
 * 主な機能：
 * - 通知のステータス更新（未読→既読）
 * - 通知タイプに応じた処理
 * - 未読通知数の更新
 * 
 * 使用方法：
 * const { handleNotification } = useNotificationHandler();
 * handleNotification(notificationData, notificationHandlers);
 */
const useNotificationHandler = () => {
  const dispatch = useDispatch();
  const notifications = useAppSelector(selectNoti);

  // 当該レコードのstatusを1（既読）に変更する関数
  // DBのstatusを1に変更してから、Reduxのstatusも1に更新する。さらにvaluesのstatusも1に更新する。
  const updateStatus = async (values: any) => {
    const notificationId = values?.id;
    if (!notificationId) {
      return false;
    }
    const res = await notiApi.updateStatusNotification(notificationId);
    if (res.status === 200) {
      dispatch(updateNotificationStatus({ id: notificationId, status: 1 }));  // 当該レコードのみを「読んだ」状態に変更する
      values.status = 1;
      dispatch(updateNumberNoti(notifications.numberNoti === 0 ? notifications.numberNoti : notifications.numberNoti - 1));
      
      return true;
    }
    return false;
  };

  // クリック時の処理を行う関数
  // 通知のtypeに応じて各タイプの処理を行う（未読の場合はDBを既読に変更してから）。
  const handleNotification = async (values: any, handlers: NotificationHandlers) => {
    // typeに関係なく既読更新を試みる。
    await updateStatus(values); // This updates the notification as "read"

    // 通知のtypeが空 or 不正な場合でも既読更新は行う。
    const rawType = values?.type?.type;
    /*
     * 貸出・予約の通知は data に type キーを持たず、kind で種類を表す形で届く。
     *   {borrowing_id, item, kind: "start" | "end", moment}
     *   {borrowingId, borrowingableId, borrowingableType, kind: "reservation_day_before_start", reservation_start}
     * これらは以前 "unexpected_type" と判定され、タップしても何も起きなかった
     * （2026-09-18 に動作ログで検出。直近30日で134件が該当）。
     * いずれも貸出状況の画面へ遷移させるのが適切なため return_vehicle として扱う。
     */
    const type = rawType ?? (
      values?.type?.kind
        ? "return_vehicle"
        : typeof values?.type === "number"
          ? values.type
          : Array.isArray(values?.type) && values.type.length === 0
            ? "no_type"
            : values?.type === null || values?.type === undefined
              ? "no_type"
              : "unexpected_type"
    );

    if (type === "unexpected_type") {
      console.error("通知のtypeが予期せぬ値になっています。", values);
    }

    const handler = handlers[type] || handlers["no_type"];

    if (values.status === FilterNotificationStatusEnums.ENUMS_STATUS_READ) {
      handler?.read(values);
    } else {
      handler?.unread(values);
    }
  };

  return { handleNotification };
};

export const NotificationView = () => {
  // redux state
  const notifications = useAppSelector(selectNoti);
  const user = useAppSelector(selectAuth);
  const { userInfo } = user;
  // component state
  const [radioValue, setRadioValue] = React.useState<any>(FilterNotificationStatusEnums.ENUMS_STATUS_UNREAD);
  const [typeValue, setTypeValue] = React.useState<number>(FilterNotificationTypeEnums.TYPE_ALL);
  const [loadMore, setLoadMore] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [reload, setReload] = React.useState<any>();
  const [selectedNotifications, setSelectedNotifications] = React.useState<any>([]);

  // hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { handleNotification } = useNotificationHandler();
  
  /*
   * 通知タイプごとのハンドラー定義
   * もし新たな通知タイプが追加された場合は、ここにハンドラーを追加する。
   */
  const notificationHandlers: NotificationHandlers = {
    // 機器返却
    [NotificationTypeEnums.TYPE_INSTRUMENT]: {
      read: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      },
      unread: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      }
    },
    // 車両返却
    [NotificationTypeEnums.TYPE_VEHICLE]: {
      read: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      },
      unread: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      }
    },
    // 申請
    [NotificationTypeEnums.TYPE_REQUEST_OT]: {
      read: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}/${values.otID}?noti=${values.id}&status=${values.status}`);
      },
      unread: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}/${values.otID}?noti=${values.id}&status=${values.status}`);
      }
    },
    // 日報
    [NotificationTypeEnums.TYPE_REQUEST_TIMESHEET]: {
      read: (values) => {
        if (values.type?.creator === userInfo?.id) {
          if (values.type?.status === ApproveTypeEnums.REJECTED) {
            history.push(`${appRouteEnums.PATH_TIMESHEET}/edit/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
          } else {
            history.push(`${appRouteEnums.PATH_TIMESHEET}/detail/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
          }
        } else {
          history.push(`${appRouteEnums.PATH_TIMESHEET}/detail/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
        }
      },
      unread: (values) => {
        if (values.type?.creator === userInfo?.id) {
          if (values.type?.status === ApproveTypeEnums.REJECTED) {
            history.push(`${appRouteEnums.PATH_TIMESHEET}/edit/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
          } else {
            history.push(`${appRouteEnums.PATH_TIMESHEET}/detail/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
          }
        } else {
          history.push(`${appRouteEnums.PATH_TIMESHEET}/detail/${values.timesheetType}/${values.tsID}?noti=${values.id}&status=${values.status}`);
        }
      }
    },
    // 臨時の残業らしい
    'change_temporary_to_offical': {
      read: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}/edit/${values?.type?.otID}`);
      },
      unread: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}/${values?.type?.otID}`);
      }
    },
    // 車両返却なんだろう
    'return_vehicle': {
      read: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      },
      unread: (values) => {
        history.push(`/${NotificationLinkEnums.RETURN_VEHICLE}?noti=${values.id}&status=${values.status}`);
      }
    },
    // 申請の承認要請（Cronで作成される）
    [NotificationTypeEnums.TYPE_REQUEST_OT_CRON]: {
      read: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}`);
      },
      unread: (values) => {
        history.push(`${appRouteEnums.PATH_OVERTIME}`);
      }
    },
    // 日報の承認要請（Cronで作成される）
    [NotificationTypeEnums.TYPE_REQUEST_TIMESHEET_CRON]: {
      read: (values) => {
        history.push(`${appRouteEnums.PATH_TIMESHEET}`);
      },
      unread: (values) => {
        history.push(`${appRouteEnums.PATH_TIMESHEET}`);
      }
    },
    [NotificationTypeEnums.TYPE_SCHEDULE]: {
      read: (values) => {
        const eventId = values.type?.reservation_id;
        if(eventId) {
          history.push({
            pathname: appRouteEnums.PATH_SCHEDULE,
            search: `?eventId=${eventId}`,
          });
        }
      },
      unread: (values) => {
        const eventId = values.type?.reservation_id;
        if(eventId) {
          history.push({
            pathname: appRouteEnums.PATH_SCHEDULE,
            search: `?eventId=${eventId}`,
          });
        }
      }
    },
    // dataカラムが[]の場合。実は色んな種類がある
    'no_type': {
      read: () => {
        // 何もしない（Linterが怒るので記載）
      },
      unread: () => {
        // 何もしない（Linterが怒るので記載） 
      }
    },
  };
  
  //what: events
  const handleOnSelectNoti = async (values) => {
    await handleNotification(values, notificationHandlers);
  };

  const handleDeleteNoti = async (id: any) => {
    try {
      await notiApi.deleteNotification({
        id: id,
      });
      dispatch(resetNoti());
      setReload(!reload);
    } catch (err) { }
  };
  const handleToggleSelectNoti = (id: string) => {
    setSelectedNotifications((prevSelected) => {
      if (prevSelected.includes(id)) {
        // If the ID is already in the selected list, remove it
        return prevSelected.filter((selectedId) => selectedId !== id);
      } else {
        // If the ID is not in the selected list, add it
        return [...prevSelected, id];
      }
    });
  };
  const handleDeleteMultipleNoti = async () => {
    if (selectedNotifications.length === 0) {
      return;
    }
    try {
      await notiApi.deleteMultipleNotification({
        ids: selectedNotifications,
      });

      setReload(!reload);
      setIsSelected(false);
      dispatch(resetNoti());
      setSelectedNotifications([]);
    } catch (err) { }
  };
  const fetchListNoti = async () => {
    try {
      setLoading(true);
      debugLog("info", "通知一覧", `取得開始 page=${page} status=${radioValue} type=${typeValue}`);

      const res = await notiApi.viewListNotification({
        page: page,
        status: parseInt(radioValue) === FilterNotificationStatusEnums.ENUMS_STATUS_ALL ? "" : radioValue,
        type_noti: typeValue === FilterNotificationTypeEnums.TYPE_ALL ? "" : typeValue,
      });
      debugLog("info", "通知一覧", `取得完了 ${res.data?.data?.data?.length ?? 0}件 未読総数=${res.data?.data?.amount}`);
      if (page === 1) {
        dispatch(resetNoti());
      }
      dispatch(updateNoti(res.data.data.data));
      dispatch(updateNumberNoti(res.data.data.amount));
      setLoading(false);
      if (!res.data.data.paging.next_page_url) {
        setLoadMore(false);
      } else {
        setLoadMore(true);
      }
    } catch (err: any) {
      debugLog("error", "通知一覧", `取得失敗 ${err?.response?.status ?? ""} ${err?.message ?? err}`);
      setLoading(false);
    }
  };
  const handleLoadMore = () => {
    setPage(page + 1);
  };
  React.useEffect(() => {
    fetchListNoti();
    return () => {
      //    do something cleanup
    };
  }, [page, radioValue, reload, typeValue]);
  React.useEffect(() => {
    debugLog("info", "通知一覧", "画面を表示した（マウント）");
    return () => {
      debugLog("info", "通知一覧", "画面を離れた（アンマウント）");
      dispatch(resetNoti());
    };
  }, []);

  /*
   * 描画状態の計測（検証用・dev環境限定）
   *
   * 「データは取得できているのにカードが画面に出ない」事象を切り分けるため、
   * リストの件数が変わったあと複数のタイミングでDOMを実測する。
   * 子要素数が10なのに高さが0、または画面外にある場合は描画側の問題と判定できる。
   */
  React.useEffect(() => {
    const count = notifications.listNoti?.length ?? 0;
    const timers = [0, 300, 1200].map((ms) =>
      setTimeout(() => {
        // stored=Reduxが持っている件数 / loading=取得中か。正常な結果はサーバーへ送られない
        measureElement("通知一覧", ".noti__content", { elapsed: ms, stored: count, loading });
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [notifications.listNoti, loading]);
  const [isSelected, setIsSelected] = React.useState(false);

  React.useEffect(() => {
    if (isSelected) {
      setSelectedNotifications(notifications.listNoti.map((noti) => noti.id));
    } else {
      setSelectedNotifications([]);
    }
  }, [notifications.listNoti, isSelected]);
  
  const handleSelectAll = (event) => {
    setIsSelected(event.target.checked);
  };


  return (
    <StyledNotificationComponent>
      {/* <AppTopBar /> */}
      <Divider />
      <CircularLoader loading={loading} />
      <div style={{ textAlign: "left" }}>
        <h1 className="title-text">通知</h1>
        <DocumentBorder />
      </div>


      <div className="wrapperForm">
        {/* <p className="top-icon" onClick={() => history.push(appRouteEnums.PATH_HOME)}>
          <AppIconButton icon={<img src={mobile_arrow_left} alt="icon" />} />
        </p> */}
        <div className="form" style={{ width: "100%" }}>
        <div style={{ textAlign: 'left', marginTop: '0.5rem', marginLeft: '5px'}}><strong>種別:</strong></div>
          <div className="form__buttons">
           
              
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_ALL}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_ALL);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_ALL_DISPLAY}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_REQUEST_OT}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_REQUEST_OT);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_REQUEST_OT_DISPLAY}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_REQUEST_TIMESHEET}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_REQUEST_TIMESHEET);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_REQUEST_TIMESHEET_DISPLAY}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_RESERVATION}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_RESERVATION);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_RESERVATION_DISPLAY}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_SCHEDULE}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_SCHEDULE);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_SCHEDULE_DISPLAY}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={typeValue === FilterNotificationTypeEnums.TYPE_OTHERS}
                        onClick={() => {
                            setTypeValue(FilterNotificationTypeEnums.TYPE_OTHERS);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationTypeNameEnums.TYPE_OTHERS_DISPLAY}
                    </StyledToggleButtonShort>
        
         </div>

         <div style={{ textAlign: 'left', marginTop: '0.5rem', marginLeft: '5px'}}><strong>ステータス:</strong></div>
                
         <div className="form__buttons">
                    <StyledToggleButtonShort
                        isActive={radioValue === FilterNotificationStatusEnums.ENUMS_STATUS_ALL}
                        onClick={() => {
                            setRadioValue(FilterNotificationStatusEnums.ENUMS_STATUS_ALL);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationEnums.ENUMS_FILTER_ALL}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={radioValue === FilterNotificationStatusEnums.ENUMS_STATUS_UNREAD}
                        onClick={() => {
                            setRadioValue(FilterNotificationStatusEnums.ENUMS_STATUS_UNREAD);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationEnums.ENUMS_FILTER_UNREAD}
                    </StyledToggleButtonShort>
                    <StyledToggleButtonShort
                        isActive={radioValue === FilterNotificationStatusEnums.ENUMS_STATUS_READ}
                        onClick={() => {
                            setRadioValue(FilterNotificationStatusEnums.ENUMS_STATUS_READ);
                            setPage(1);
                        }}
                    >
                        {FilterNotificationEnums.ENUMS_FILTER_READ}
                    </StyledToggleButtonShort>
            
            
          </div>
        </div>


        <div>
          {(selectedNotifications.length > 0 || true) && (
            <span onClick={handleDeleteMultipleNoti}
              style={{ display: "flex", justifyContent: "center", marginTop: "1rem", marginBottom: "1rem", filter: selectedNotifications.length === 0 ? "brightness(0.5) grayscale(1)" : "" }}
            >
              <NextButton>
                選択した通知を削除
              </NextButton>
            </span>

          )
          }
          <div style={{ display: "flex", alignItems: "center", paddingTop: "1rem" }}>

            <div
              style={{
                margin: "0px 0px 10px 0px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleSelectAll}
                className="checkBoxAll"
              />
              <div style={{ marginLeft: "10px" }}>全てを選択</div>
            </div>

          </div>

        </div>

      </div>


      <div className="noti" style={{ marginTop: '2rem' }}>
        <div className="noti__wrapper">
          <div className="noti__content">
            {notifications.listNoti?.length
              ? notifications.listNoti.map((noti, index) => (
                <div key={index}>
                  <NotificationItem
                    handleOnSelectNoti={handleOnSelectNoti}
                    data={noti}
                    handleDeleteNoti={() => handleDeleteNoti(noti.id)}
                    isSelected={selectedNotifications.includes(noti.id)}
                    handleToggleSelect={() => handleToggleSelectNoti(noti.id)}
                  />
                </div>
              ))
              : !loading && <p className="messageNotFound">{MESSAGE_DATA_NOT_FOUND}</p>}
          </div>
        </div>
      </div>
      {/* <div className="wrapperLoading">
                <CircularLoader loading={loading} size={15} type="fullContent" />
            </div> */}
      {!loading && loadMore && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <span className="viewMore__text" onClick={() => handleLoadMore()}>
            <NextButton>
              {/* <img className="viewMore__icon" src={active_arrow_down} alt="icon" onClick={() => handleLoadMore()} /> */}
              ↓続きを見る
            </NextButton>
          </span>
        </div>
      )}
    </StyledNotificationComponent>
  );
};
